import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import api from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';
import { GraduationCap, Loader2, Printer, Users, Copy, Check, Ticket, ShoppingCart, CreditCard, Eye, X, FileText, Mail, TrendingUp, Award, ArrowRight, ChevronDown } from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/format';
import FormateurTracking from './formateur/FormateurTracking';
import FormateurPackCheckoutModal from '../../components/stripe/FormateurPackCheckoutModal';

const DUREE_LABELS = { '1m': '1 mois', '2m': '2 mois', '6m': '6 mois', '12m': '12 mois' };
const DUREE_ORDER = { '1m': 1, '2m': 2, '6m': 6, '12m': 12 };

/**
 * Espace formateur — distribuer des codes éditoriaux à ses apprenants et suivre leurs
 * résultats (lecture seule, scopé, apprenants consentants). 3 onglets : Suivi · Mes codes ·
 * Acheter des codes (packs de sièges configurés en admin). Self-service : "Rejoindre".
 */
export default function FormateurSuivi() {
    usePageTitle('Espace formateur');
    const { user, fetchProfile } = useAuthStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [cohorts, setCohorts] = useState([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('suivi');
    const [joining, setJoining] = useState(false);
    const [copied, setCopied] = useState(null);
    const [purchaseFlash, setPurchaseFlash] = useState(false);

    const loadCohort = () => {
        setLoading(true);
        api.get('/formateur/cohort.php')
            .then(({ data }) => { setCohorts(data.data?.cohorts || []); setTotal(data.data?.total_students || 0); })
            .catch(e => setError(e.response?.data?.message || 'Erreur'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!user?.is_formateur) { setLoading(false); return; }
        loadCohort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.is_formateur]);

    // Retour Stripe : ?formateur_purchase=success → recharge + bascule sur "Mes codes"
    useEffect(() => {
        if (searchParams.get('formateur_purchase') === 'success') {
            setPurchaseFlash(true);
            setTab('codes');
            if (user?.is_formateur) loadCohort(); else fetchProfile();
            const next = new URLSearchParams(searchParams);
            next.delete('formateur_purchase'); next.delete('session_id');
            setSearchParams(next, { replace: true });
            setTimeout(() => setPurchaseFlash(false), 8000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!user) return <Navigate to="/connexion?redirect=/formateur" replace />;

    const handleJoin = async () => {
        setJoining(true);
        setError(null);
        try {
            await api.post('/formateur/join.php');
            await fetchProfile();
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur');
        }
        setJoining(false);
    };

    if (!user.is_formateur) {
        return (
            <div className="auth-page" style={{ minHeight: '55vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ maxWidth: 520, textAlign: 'center' }}>
                    <GraduationCap size={44} style={{ color: 'var(--primary)', marginBottom: 12 }} />
                    <h1 style={{ fontSize: '1.5rem', marginBottom: 8 }}>Espace formateur</h1>
                    <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
                        Vous préparez des candidats aux concours ? Rejoignez l'espace formateur pour donner accès
                        à vos apprenants à nos QCM (par code : ils créent un compte gratuit, sans abonnement à payer) et <strong>suivre leurs
                        résultats</strong> — avec rapports exportables.
                    </p>
                    {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
                    <button className="btn btn-primary" onClick={handleJoin} disabled={joining}>
                        {joining ? <Loader2 size={16} className="spin" /> : <GraduationCap size={16} />} Rejoindre l'espace formateur
                    </button>
                    <p style={{ fontSize: '0.78em', color: 'var(--text-muted)', marginTop: 12 }}>
                        Gratuit — vous payez seulement les codes que vous distribuez à vos apprenants.
                    </p>
                </div>
            </div>
        );
    }

    const rateColor = (r) => r === null ? 'var(--text-muted)' : r >= 70 ? '#22c55e' : r >= 50 ? '#f59e0b' : '#ef4444';
    const copyLink = (code) => {
        const url = `${window.location.origin}/compte/code?code=${encodeURIComponent(code)}`;
        navigator.clipboard.writeText(url);
        setCopied(code);
        setTimeout(() => setCopied(null), 1800);
    };

    return (
        <div className="formateur-page" style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 16px 60px' }}>
            <style>{`@media print { .formateur-print-hide { display: none !important; } }`}</style>

            {/* Bandeau de présentation sur tous les onglets ; il porte le titre de la page. */}
            <FormateurHero onBuy={tab !== 'acheter' && !loading && cohorts.length === 0 ? () => setTab('acheter') : null} />

            {purchaseFlash && (
                <div className="alert" style={{ marginBottom: 16, background: 'rgba(34,197,94,0.1)', color: '#15803d', border: '1px solid rgba(34,197,94,0.3)' }}>
                    <Check size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Paiement reçu ! Votre code apparaît dans « Mes codes » (quelques secondes si besoin, rechargez).
                </div>
            )}

            <div className="formateur-print-hide" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--card-border)', marginBottom: 22, flexWrap: 'wrap' }}>
                {[['suivi', 'Suivi de mes apprenants'], ['codes', 'Mes codes'], ['acheter', 'Acheter des codes']].map(([k, label]) => (
                    <button key={k} onClick={() => setTab(k)}
                        style={{
                            padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.92em',
                            borderBottom: tab === k ? '2px solid var(--primary)' : '2px solid transparent',
                            color: tab === k ? 'var(--primary)' : 'var(--text-muted)', fontWeight: tab === k ? 700 : 500,
                        }}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'acheter' ? (
                <AcheterTab onPurchased={() => { loadCohort(); setTab('codes'); }} />
            ) : loading ? (
                <div style={{ color: 'var(--text-muted)' }}><Loader2 size={16} className="spin" /> Chargement…</div>
            ) : error ? (
                <div className="alert alert-error">{error}</div>
            ) : (
                <>
                    {tab === 'suivi' && <FormateurTracking cohorts={cohorts} />}
                    {tab === 'codes' && <CodesTab cohorts={cohorts} copyLink={copyLink} copied={copied} />}
                </>
            )}

        </div>
    );
}

function EmptyCodes() {
    return (
        <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', padding: '20px 0' }}>
            Vous n'avez pas encore de codes. Rendez-vous dans « Acheter des codes » pour en obtenir, puis distribuez-les à vos apprenants.
        </div>
    );
}

/**
 * Libellé d'un code sans la thématique : il a été généré sous la forme « Thématique · 10 apprenants · 1 mois (offert) »
 * et la thématique est déjà affichée à côté (avec son nom actuel, qui peut avoir changé depuis).
 */
const packLabel = (label) => {
    if (!label) return '';
    const parts = String(label).split(' · ');
    return parts.length >= 3 ? parts.slice(1).join(' · ') : label;
};

/** Bandeau de présentation (photo de classe ; titre et points en HTML, icônes au trait du site). */
function FormateurHero({ onBuy }) {
    return (
        <section className="fhero formateur-print-hide">
            <img className="fhero__img" src="/img/formateur-hero.webp"
                srcSet="/img/formateur-hero-1100.webp 1100w, /img/formateur-hero.webp 2000w"
                sizes="(max-width: 720px) 100vw, 1000px" alt="" loading="eager" />
            <div className="fhero__text">
                <h1 className="fhero__eyebrow">Espace formateur</h1>
                <h2 className="fhero__title">Entraînez vos apprenants, suivez chacun d’eux.</h2>
                <ul className="fhero__points">
                    <li><Users size={16} /> Suivi des apprenants</li>
                    <li><TrendingUp size={16} /> Progression par thème</li>
                    <li><Ticket size={16} /> Un code pour toute la classe</li>
                    <li><Award size={16} /> Attestations imprimables</li>
                </ul>
                {onBuy && (
                    <button type="button" className="btn btn-primary btn-sm fhero__cta" onClick={onBuy}>
                        Choisir un pack <ArrowRight size={14} />
                    </button>
                )}
            </div>
        </section>
    );
}

function CodesTab({ cohorts, copyLink, copied }) {
    if (cohorts.length === 0) return <EmptyCodes />;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.88em', margin: '0 0 6px' }}>
                Distribuez ces liens à vos apprenants : ils créent un compte gratuit, activent le code (aucun abonnement à payer) et leurs résultats remontent dans « Suivi de mes apprenants ».
            </p>
            {cohorts.map(c => (
                <div key={c.code_id} style={{ border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Ticket size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                    <code style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}>{c.code}</code>
                    <span style={{ fontSize: '0.85em' }}>{c.scope_label}{c.label ? ` · ${packLabel(c.label)}` : ''}</span>
                    <span style={{ fontSize: '0.82em', color: 'var(--text-muted)' }}>
                        {c.used_count}/{c.max_uses} activés · {DUREE_LABELS[c.duree] || c.duree}
                    </span>
                    <button className="btn btn-sm btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => copyLink(c.code)}>
                        {copied === c.code ? <Check size={14} /> : <Copy size={14} />} Copier le lien
                    </button>
                </div>
            ))}
        </div>
    );
}

function AcheterTab({ onPurchased }) {
    const [packs, setPacks] = useState([]);
    const [infos, setInfos] = useState({}); // fiche de chaque thématique (description, contenu en ligne, matières)
    const [ouverts, setOuverts] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const basculer = (id) => setOuverts(prev => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    useEffect(() => {
        api.get('/formateur/packs.php')
            .then(({ data }) => { setPacks(data.data?.packs || []); setInfos(data.data?.scopes || {}); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ color: 'var(--text-muted)' }}><Loader2 size={16} className="spin" /> Chargement…</div>;

    if (packs.length === 0) {
        return (
            <div style={{ border: '1px dashed var(--card-border)', borderRadius: 12, padding: '28px 24px', textAlign: 'center' }}>
                <ShoppingCart size={32} style={{ color: 'var(--primary)', marginBottom: 10 }} />
                <h3 style={{ margin: '0 0 8px' }}>Aucun pack disponible pour le moment</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.92em', maxWidth: 520, margin: '0 auto' }}>
                    Écrivez-nous à <a href="mailto:contact@monquiz.fr">contact@monquiz.fr</a> pour vos accès, ou revenez bientôt.
                </p>
            </div>
        );
    }

    // Sections (éditoriales, puis communautaires) → une carte par thématique (image en fond d'en-tête)
    // → colonnes par nombre de sièges → durées croissantes.
    const fmt = (n) => {
        const d = Number(n) % 1 ? 2 : 0; // 26 € ; 2,60 €
        return `${Number(n).toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d })} €`;
    };
    const SECTIONS = [
        ['editoriale', 'Thématiques éditoriales', 'Contenu conçu et relu par MonQuiz.'],
        ['communautaire', 'Thématiques communautaires', 'Publiées par des membres de MonQuiz, validées avant publication.'],
    ];
    // Ordre par potentiel auprès des formateurs (les thématiques non listées suivent, par ordre alphabétique).
    const PRIORITY = ['crpe', 'fonction-publique-etat', 'fonction-publique-territoriale', 'atsem', 'police-gendarmerie',
        'sante-social', 'istqb-foundation', 'istqb-genai', 'istqb-ctai', 'tests-psychotechniques'];
    const rank = (slug) => { const i = PRIORITY.indexOf(slug); return i === -1 ? 99 : i; };
    const scopesOf = (kind) => {
        const map = new Map();
        packs.filter(p => (p.scope_kind || 'editoriale') === kind).forEach(p => {
            if (!map.has(p.scope_id)) map.set(p.scope_id, { id: p.scope_id, label: p.scope_label, slug: p.scope_slug, image: p.scope_image, color: p.scope_color, packs: [] });
            map.get(p.scope_id).packs.push(p);
        });
        return [...map.values()].sort((a, b) => rank(a.slug) - rank(b.slug) || a.label.localeCompare(b.label, 'fr'));
    };

    return (
        <>
            <p className="fpack-intro">
                Choisissez un pack : vous recevez un code (valable pour N apprenants) à distribuer. Vos apprenants créent un compte gratuit et activent le code (aucun abonnement à payer).
            </p>
            {SECTIONS.map(([kind, title, sub]) => {
                const scopes = scopesOf(kind);
                if (!scopes.length) return null;
                return (
                    <section key={kind} className="fpack-section">
                        <h3 className="fpack-section__title">{title}</h3>
                        <p className="fpack-section__sub">{sub}</p>
                        {scopes.map(sc => {
                            const bySeats = {};
                            sc.packs.forEach(p => { (bySeats[p.nb_seats] = bySeats[p.nb_seats] || []).push(p); });
                            const seatGroups = Object.keys(bySeats).map(Number).sort((a, b) => a - b);
                            const minSeat = Math.min(...sc.packs.map(p => p.price_eur / p.nb_seats));
                            const info = infos[sc.id];
                            const ouvert = ouverts.has(sc.id);
                            const nf = (n) => Number(n).toLocaleString('fr-FR');
                            return (
                                <article key={sc.label} className="fpack-scope" style={sc.color ? { '--scope-color': sc.color } : undefined}>
                                    <header className={`fpack-scope__head${sc.image ? ' has-image' : ''}`}
                                        style={sc.image ? { backgroundImage: `url(${sc.image})` } : undefined}>
                                        <h4>{sc.label}</h4>
                                        <div className="fpack-scope__head-actions">
                                            <span>À partir de {fmt(Math.round(minSeat * 100) / 100)} par apprenant</span>
                                            {info && (
                                                <button type="button" className={`fpack-scope__more${ouvert ? ' is-open' : ''}`}
                                                    aria-expanded={ouvert} onClick={() => basculer(sc.id)}>
                                                    {ouvert ? 'Masquer' : 'En savoir plus'} <ChevronDown size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </header>
                                    {info && ouvert && (
                                        <div className="fpack-info">
                                            <div className="fpack-info__stats">
                                                <strong>{nf(info.nb_questions)} questions</strong> en ligne, réparties en <strong>{nf(info.nb_sujets)} sujets</strong>
                                                {info.matieres?.length > 0 && <> et <strong>{info.matieres.length} matière{info.matieres.length > 1 ? 's' : ''}</strong></>}
                                                {info.partner_name && <> · publiée par <strong>{info.partner_name}</strong></>}
                                            </div>
                                            {info.description && <p className="fpack-info__desc">{info.description}</p>}
                                            {info.matieres?.length > 0 && (
                                                <ul className="fpack-info__matieres">
                                                    {info.matieres.map(m => <li key={m}>{m}</li>)}
                                                </ul>
                                            )}
                                            <a className="fpack-info__link" href={`/compte/quiz/${sc.slug}`} target="_blank" rel="noopener noreferrer">
                                                Voir les sujets de la thématique <ArrowRight size={13} />
                                            </a>
                                        </div>
                                    )}
                                    <div className="fpack-scope__body">
                                        {seatGroups.map(seats => {
                                            const seatPacks = bySeats[seats].slice().sort((a, b) => (DUREE_ORDER[a.duree] || 99) - (DUREE_ORDER[b.duree] || 99));
                                            return (
                                                <div key={seats} className="fpack-seats">
                                                    <div className="fpack-seats__title">{seats} apprenants</div>
                                                    <div className="fpack-grid">
                                                        {seatPacks.map(p => (
                                                            <div key={p.id} className="fpack-tile">
                                                                <span className="fpack-tile__duree">{DUREE_LABELS[p.duree] || p.duree}</span>
                                                                <strong className="fpack-tile__price">{fmt(p.price_eur)}</strong>
                                                                <span className="fpack-tile__unit">soit {fmt(Math.round(p.price_eur / p.nb_seats * 100) / 100)} / apprenant</span>
                                                                <button className="btn btn-sm btn-primary" onClick={() => setSelected(p)}>
                                                                    <CreditCard size={13} /> Acheter
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </article>
                            );
                        })}
                    </section>
                );
            })}
            <aside className="fpack-contact">
                <Mail size={18} />
                <div>
                    <strong>Une demande particulière ?</strong>
                    <p>
                        Plus de 25 apprenants, une durée calée sur votre session, un devis, une facture au nom de votre organisme,
                        un bon de commande ou un paiement par mandat administratif, une thématique qui n’est pas dans la liste…
                        Écrivez-nous à <a href="mailto:contact@monquiz.fr?subject=Espace%20formateur%20%3A%20demande%20particuli%C3%A8re">contact@monquiz.fr</a> : nous vous répondons rapidement.
                    </p>
                </div>
            </aside>
            {selected && (
                <FormateurPackCheckoutModal
                    pack={selected}
                    onClose={() => setSelected(null)}
                    onSuccess={() => { setSelected(null); onPurchased?.(); }}
                />
            )}
        </>
    );
}

